import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation } from 'react-query';
import { useApi } from '../../context';
import { useLenderDeclineReasonsQuery } from '../../hooks';
import { ActiveDataRoomUser } from '../../models';
import { CerebroDrawer } from '../CerebroDrawer';
import { getApiResponseData } from '../../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../utils/api';
import { CerebroHeading } from '../CerebroHeading';
import { Field, Formik } from 'formik';
import { FormikUtils } from '../../utils';
import { CerebroForm } from '../CerebroForm';
import { Grid, Skeleton } from '@mui/material';
import { CerebroLabel } from '../CerebroLabel';
import { CerebroMultiDropdown, CerebroMultiDropdownMenuItem } from '../CerebroMultiDropdown';
import { CerebroInput } from '../CerebroInput';
import { CerebroTextarea } from '../CerebroTextarea';
import { CerebroPrimaryButton } from '../CerebroPrimaryButton';
import { CerebroText } from '../CerebroText';
import * as Yup from 'yup';

interface LenderOptOutFormProps {
	dataRoomGuid: string | undefined;
	user: ActiveDataRoomUser | null | undefined;
	optedOut: () => void;
	close: () => void;
}

export const LenderOptOutForm: React.FC<LenderOptOutFormProps> = ({ dataRoomGuid, user, optedOut, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const declineReasonsQuery = useLenderDeclineReasonsQuery();
	const [showOther, setShowOther] = React.useState<boolean>(false);

	const onUpdateDeclineView = (selections: number[]): void => {
		setShowOther(selections.filter(num => num == -1).length > 0);
	};

	let OptOutFormSchema = Yup.object().shape({
		reasonDetail: Yup.string().required('Details of Reason are required'),
		otherReason: Yup.string(),
	});

	if (showOther) {
		OptOutFormSchema = OptOutFormSchema.shape({
			otherReason: Yup.string().required('Other reason is required'),
		});
	}

	const responseMutation = useMutation(
		async ({ values }: LenderOptOutFormMutationVariables) => {
			const accessPatch = {
				reasons: values.reasons,
				otherReason: values.otherReason,
				reasonDetail: values.reasonDetail,
				reEngagePoint: values.reEngagePoint,
			};

			const { data, error } = await getApiResponseData<ActiveDataRoomUser>(
				api.post(CerebroApiEndpoints.DataRoomUsers.OptOut(dataRoomGuid, user?.guid), accessPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Lender was opted out.', { variant: 'success' });
				optedOut();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={user != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				Lender Opt-Out
			</CerebroHeading>
			<CerebroHeading size="h3" gutterBottom>
				{user?.fullName} ({user?.bankName})
			</CerebroHeading>
			<Formik
				validationSchema={OptOutFormSchema}
				initialValues={{
					reasons: [] as number[],
					otherReason: '',
					reasonDetail: '',
					reEngagePoint: '',
				}}
				onSubmit={FormikUtils.MutateOnSubmit(responseMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="reasons">Opt-Out Reasons</CerebroLabel>
									{declineReasonsQuery.isLoading && <Skeleton height={60} />}
									{declineReasonsQuery.isSuccess && (
										<Field
											id="reasons"
											as={CerebroMultiDropdown}
											name="reasons"
											onClose={() => onUpdateDeclineView(values.reasons)}
											disabled={isSubmitting}
										>
											{declineReasonsQuery.data.map(reason => (
												<CerebroMultiDropdownMenuItem key={reason.guid} value={reason.id}>
													{reason.name}
												</CerebroMultiDropdownMenuItem>
											))}
										</Field>
									)}
								</Grid>
								{showOther && (
									<Grid item xs={8}>
										<CerebroLabel id="otherReason" required>
											Other Reason
										</CerebroLabel>
										<Field
											id="otherReason"
											name="otherReason"
											as={CerebroInput}
											error={FormikUtils.IsInvalid('otherReason', errors, touched)}
										/>
										{FormikUtils.IsInvalid('otherReason', errors, touched) && (
											<CerebroText variant="error">{errors.otherReason}</CerebroText>
										)}
									</Grid>
								)}
								<Grid item xs={8}>
									<CerebroLabel id="reasonDetail" required>
										Details of Reason
									</CerebroLabel>
									<Field
										id="reasonDetail"
										as={CerebroTextarea}
										name="reasonDetail"
										disabled={isSubmitting}
									/>
									{FormikUtils.IsInvalid('reasonDetail', errors, touched) && (
										<CerebroText variant="error">{errors.reasonDetail}</CerebroText>
									)}
								</Grid>
								<Grid item xs={8}>
									<CerebroLabel id="reEngagePoint">Re-Engage Point</CerebroLabel>
									<Field
										id="reEngagePoint"
										as={CerebroTextarea}
										name="reEngagePoint"
										disabled={isSubmitting}
									/>
									{FormikUtils.IsInvalid('reEngagePoint', errors, touched) && (
										<CerebroText variant="error">{errors.reEngagePoint}</CerebroText>
									)}
								</Grid>
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type LenderOptOutFormMutationVariables = FormikUtils.MutateOnSubmitVariables<LenderOptOutFormValues>;
interface LenderOptOutFormValues {
	reasons: number[];
	otherReason: string;
	reasonDetail: string;
	reEngagePoint: string;
}
